import { Input } from '@/components/ui/input';
import { useState, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

interface TargetCPAInputProps {
  targetCpa: number | undefined;
  setTargetCpa: (value: number) => void;
  disabled?: boolean;
  conversionTrackingEnabled?: boolean;
}

export const TargetCPAInput: React.FC<TargetCPAInputProps> = ({
  targetCpa,
  setTargetCpa,
  disabled = false,
  conversionTrackingEnabled = true,
}) => {
  const { symbol } = useCurrencySymbol();
  const [inputValue, setInputValue] = useState<string>('');
  const [error, setError] = useState<string>('');
  const isFocused = useRef(false);

  // Disable only when conversion tracking is off AND value is already 0
  const hasValue = targetCpa !== undefined && targetCpa > 0;
  const isDisabled = disabled || (!conversionTrackingEnabled && !hasValue);

  // targetCpa is stored in cents, display in dollars
  const centsToDollars = (cents: number | undefined): string => {
    if (cents === undefined || cents === 0) return '';
    return (cents / 100).toFixed(2);
  };

  const dollarsToCents = (dollars: number): number => {
    return Math.round(dollars * 100);
  };

  // Only sync from parent when not actively editing
  useEffect(() => {
    if (!isFocused.current) {
      setInputValue(centsToDollars(targetCpa));
    }
  }, [targetCpa]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setError('');
  };

  const handleFocus = () => {
    isFocused.current = true;
  };

  const handleBlur = () => {
    isFocused.current = false;
    const value = inputValue;
    if (value === '' || parseFloat(value) === 0) {
      setTargetCpa(0);
      setInputValue('');
      setError('');
      return;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }
    if (numValue < 0.01) {
      setError(`Target Cost Per Acquisition must be at least ${symbol}0.01`);
      return;
    }
    if (numValue > 10000) {
      setError(`Target Cost Per Acquisition cannot exceed ${symbol}10,000`);
      return;
    }
    setTargetCpa(dollarsToCents(numValue));
    setError('');
  };

  return (
    <div>
      <div className='flex items-center gap-3'>
        <div className='relative' style={{ minWidth: '100px' }}>
          <span className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDisabled ? 'text-gray-300' : 'text-gray-500'}`}>
            {symbol}
          </span>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            max="10000"
            value={inputValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder="0.00"
            disabled={isDisabled}
            className={`!ring-inset !ring-transparent text-gray-900 pl-7 ${error ? 'border-red-500' : ''}`}
          />
        </div>
        <p className="text-sm text-gray-500">
          {conversionTrackingEnabled
            ? 'Target Cost Per Acquisition (optional). The average amount to pay per conversion.'
            : (
              <>
                Target Cost Per Acquisition (optional). Requires{' '}
                <Link to="/assets" hash="google" className="text-[#290BD9] hover:text-[#2309C5] underline">
                  conversion tracking
                </Link>
                {' '}to be set up.
              </>
            )}
        </p>
      </div>
      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};
